import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const STATUS_COLOR = {
  CONNECTED: "bg-emerald-100 text-emerald-800",
  "READY TO CONNECT": "bg-amber/15 text-amber",
  "ACTION REQUIRED": "bg-red-100 text-red-700",
  ERROR: "bg-red-100 text-red-700",
};

export default function Overview() {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    apiFetch("/admin/overview").then(setData).catch(() => {});
    apiFetch("/admin/funnel-stats").then(setFunnel).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm text-ink/50">Loading…</p>;

  const newLeads = data.leads_by_status?.NEW || 0;
  const hot = data.leads_by_status?.HOT || 0;
  const followUp = data.leads_by_status?.["FOLLOW-UP DUE"] || 0;
  const converted = data.leads_by_status?.CONVERTED || 0;

  return (
    <div data-testid="admin-overview">
      <p className="eyebrow">Overview</p>
      <h1 className="mt-2 font-serif text-3xl tracking-tight text-ink sm:text-4xl" data-testid="overview-greeting">
        {greeting()}, <span className="italic text-burgundy">Dr. Irma</span>
      </h1>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "New Leads", value: newLeads, testId: "stat-new-leads" },
          { label: "Hot", value: hot, testId: "stat-hot-leads" },
          { label: "Follow-up Due", value: followUp, testId: "stat-followup" },
          { label: "Converted", value: converted, testId: "stat-converted" },
        ].map((s) => (
          <div key={s.label} className="border border-ink/10 bg-warmwhite p-6" data-testid={s.testId}>
            <p className="font-serif text-4xl text-ink">{s.value}</p>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 grid gap-8 lg:grid-cols-2">
        <section className="border border-ink/10 bg-warmwhite p-8" data-testid="overview-content">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">
            Today&apos;s content opportunities
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4 text-center">
            {["dental", "consulting", "publishing"].map((v) => (
              <div key={v} className="border border-ink/10 p-5">
                <p className="font-serif text-3xl text-ink">{data.content_by_vertical?.[v] || 0}</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/50">{v}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs leading-relaxed text-ink/50">
            Daily AI content suggestions (3 per vertical) activate when the AI
            model integration is connected. All suggestions require your
            approval before publishing.
          </p>
        </section>
        <section className="border border-ink/10 bg-warmwhite p-8" data-testid="overview-integrations">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Integrations</p>
          <ul className="mt-6 space-y-3">
            {Object.entries(data.integrations || {}).map(([key, intg]) => (
              <li key={key} className="flex items-center justify-between gap-4 text-sm">
                <span className="text-ink/75">{intg.label}</span>
                <span className={`px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.15em] ${STATUS_COLOR[intg.status] || "bg-softgrey text-ink/60"}`} data-testid={`integration-${key}`}>
                  {intg.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {funnel && (
        <section className="mt-8 border border-ink/10 bg-warmwhite p-8" data-testid="overview-funnel">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Funnel — what converts</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {[
              { label: "Appointment Requests", value: funnel.appointment_requests, testId: "funnel-appt-requests" },
              { label: "Appointments Pending", value: funnel.appointments_pending, testId: "funnel-appointments" },
              { label: "HOT Leads", value: funnel.hot_leads, testId: "funnel-hot-leads" },
              { label: "RM15 CTA Clicks", value: funnel.gf_clicks, testId: "funnel-gf-clicks" },
              { label: "RM15 CTA Views", value: funnel.gf_impressions, testId: "funnel-gf-impressions" },
              { label: "WhatsApp Clicks", value: funnel.whatsapp_clicks, testId: "funnel-wa-clicks" },
            ].map((s) => (
              <div key={s.label} className="border border-ink/10 p-5" data-testid={s.testId}>
                <p className="font-serif text-3xl text-ink">{s.value}</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/50">{s.label}</p>
              </div>
            ))}
          </div>
          {(funnel.appts_by_source || []).length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/50">Appointment requests by source</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {funnel.appts_by_source.map((r) => (
                  <span key={r.source} className="border border-ink/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/60" data-testid={`funnel-appt-source-${r.source}`}>
                    {r.source}: {r.count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {funnel.gf_by_article.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/50">RM15 clicks by article</p>
              <ul className="mt-3 space-y-2">
                {funnel.gf_by_article.map((r) => (
                  <li key={r.article} className="flex items-center justify-between gap-4 border-b border-ink/5 pb-2 text-sm" data-testid={`funnel-article-${r.article.slice(0, 20)}`}>
                    <span className="truncate text-ink/75">{r.article}</span>
                    <span className="font-serif text-lg text-burgundy">{r.clicks}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {funnel.gf_by_source.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {funnel.gf_by_source.map((r) => (
                <span key={r.source} className="border border-ink/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/60" data-testid={`funnel-source-${r.source}`}>
                  {r.source}: {r.clicks}
                </span>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
